import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { hasLastMessagePreview } from '@/modules/chat-matrix-fast/constants/chat'

const PREVIEW_STORAGE_KEY = 'chat-matrix-fast:list-previews-v1'
const EMPTY_STORAGE_KEY = 'chat-matrix-fast:list-preview-empty-v1'
const MAX_ENTRIES = 120
const EMPTY_TTL_MS = 30 * 60_000

type StoredPreview = {
  last_message: IMessage
  last_message_id?: number | string
  last_matrix_event_id?: string
  at: number
}

type PreviewStore = Record<string, StoredPreview>
type EmptyStore = Record<string, number>

function readJson<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') {
    return fallback
  }

  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      return fallback
    }
    const parsed = JSON.parse(raw) as T
    return parsed && typeof parsed === 'object' ? parsed : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // quota / private mode
  }
}

function slimMessage(message: IMessage): IMessage {
  return {
    id: message.id,
    content: message.content,
    created_at: message.created_at,
    matrix_event_id: message.matrix_event_id,
    author: message.author,
    author_id: message.author_id,
    author_name: message.author_name,
    attachments: (message.attachments || []).map(attachment => ({
      id: attachment.id,
      file: attachment.file,
      mime_type: attachment.mime_type,
      name: attachment.name,
      thumbnail: attachment.thumbnail,
    })),
    created_by: message.created_by
      ? {
          id: message.created_by.id,
          full_name: message.created_by.full_name,
          user_name: message.created_by.user_name,
          username: message.created_by.username,
        }
      : undefined,
    is_read: message.is_read,
    is_delivered: message.is_delivered,
    is_deleted: message.is_deleted,
  }
}

export function loadPersistedChatListPreview(
  chatId: number | string,
): {
  last_message: IMessage
  last_message_id?: number | string
  last_matrix_event_id?: string
} | null {
  const entry = readJson<PreviewStore>(PREVIEW_STORAGE_KEY, {})[String(chatId)]
  if (!entry?.last_message || !hasLastMessagePreview(entry.last_message)) {
    return null
  }

  return {
    last_message: entry.last_message,
    last_message_id: entry.last_message_id ?? entry.last_message.id,
    last_matrix_event_id: entry.last_matrix_event_id ?? entry.last_message.matrix_event_id,
  }
}

export function persistChatListPreview(
  chatId: number | string,
  lastMessage: IMessage | null | undefined,
  lastMessageId?: number | string,
  lastMatrixEventId?: string,
): void {
  if (!hasLastMessagePreview(lastMessage) || !lastMessage) {
    return
  }

  const store = readJson<PreviewStore>(PREVIEW_STORAGE_KEY, {})
  store[String(chatId)] = {
    last_message: slimMessage(lastMessage),
    last_message_id: lastMessageId ?? lastMessage.id,
    last_matrix_event_id: lastMatrixEventId ?? lastMessage.matrix_event_id,
    at: Date.now(),
  }

  const trimmed = Object.fromEntries(
    Object.entries(store)
      .sort((a, b) => (b[1].at ?? 0) - (a[1].at ?? 0))
      .slice(0, MAX_ENTRIES),
  )
  writeJson(PREVIEW_STORAGE_KEY, trimmed)

  const empty = readJson<EmptyStore>(EMPTY_STORAGE_KEY, {})
  if (empty[String(chatId)]) {
    delete empty[String(chatId)]
    writeJson(EMPTY_STORAGE_KEY, empty)
  }
}

export function persistChatListPreviewEmpty(chatId: number | string): void {
  const empty = readJson<EmptyStore>(EMPTY_STORAGE_KEY, {})
  empty[String(chatId)] = Date.now()
  const trimmed = Object.fromEntries(
    Object.entries(empty)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_ENTRIES),
  )
  writeJson(EMPTY_STORAGE_KEY, trimmed)
}

export function isPersistedChatListPreviewEmpty(
  chatId: number | string,
  ttlMs = EMPTY_TTL_MS,
): boolean {
  const at = readJson<EmptyStore>(EMPTY_STORAGE_KEY, {})[String(chatId)]
  if (!at) {
    return false
  }

  return Date.now() - at < ttlMs
}
